import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '../lib/utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-dialog-overlay/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * Context for scroll-aware footer shadow and header close button coordination.
 * - `canScrollDown`: DialogScrollBody sets this so DialogFooter shows an upward shadow.
 * - `hasHeaderCloseButton`: DialogHeader sets this so DialogContent hides
 *   the absolute close button (the header renders its own inline close button).
 */
const DialogScrollContext = React.createContext<{
  canScrollDown: boolean;
  setCanScrollDown: (v: boolean) => void;
  hasHeaderCloseButton: boolean;
  setHasHeaderCloseButton: (v: boolean) => void;
  hideCloseButton: boolean;
}>({
  canScrollDown: false,
  setCanScrollDown: () => {},
  hasHeaderCloseButton: false,
  setHasHeaderCloseButton: () => {},
  hideCloseButton: false,
});

function DialogScrollProvider({
  children,
  hideCloseButton = false,
}: {
  children: React.ReactNode;
  hideCloseButton?: boolean;
}) {
  const [canScrollDown, setCanScrollDown] = React.useState(false);
  const [hasHeaderCloseButton, setHasHeaderCloseButton] = React.useState(false);
  const value = React.useMemo(
    () => ({
      canScrollDown,
      setCanScrollDown,
      hasHeaderCloseButton,
      setHasHeaderCloseButton,
      hideCloseButton,
    }),
    [canScrollDown, hasHeaderCloseButton, hideCloseButton],
  );
  return <DialogScrollContext.Provider value={value}>{children}</DialogScrollContext.Provider>;
}

/** Base close button styles shared by both the absolute fallback and the muted header inline button. */
const closeButtonBase =
  'shrink-0 flex cursor-pointer items-center justify-center rounded-full text-muted-foreground ring-offset-background transition-colors hover:bg-muted-foreground/20 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none';
/** Absolute close button (non-muted dialogs): h-8 w-8 (32px) */
const closeButtonAbsolute = cn('h-8 w-8', closeButtonBase);
/** Inline close button (muted header): 32px hover target with -my-1 so header row height stays 24px effective */
const closeButtonInline = cn('-my-1 h-8 w-8', closeButtonBase);

/** Inner component that reads DialogScrollContext to conditionally render the absolute close button. */
function DialogCloseButton() {
  const { hasHeaderCloseButton } = React.useContext(DialogScrollContext);
  if (hasHeaderCloseButton) return null;
  return (
    <DialogPrimitive.Close
      data-testid="dialog-x-close-btn"
      className={cn('absolute right-4 top-4 z-20', closeButtonAbsolute)}
    >
      <X className="h-4 w-4" />
      <span className="sr-only">Close</span>
    </DialogPrimitive.Close>
  );
}

const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { hideCloseButton?: boolean }
>(({ className, children, hideCloseButton, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] overflow-hidden border bg-background p-6 shadow-lg duration-200 [&>*]:min-w-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-lg',
        className,
      )}
      {...props}
    >
      <DialogScrollProvider hideCloseButton={hideCloseButton}>
        {children}
        {!hideCloseButton && <DialogCloseButton />}
      </DialogScrollProvider>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  variant,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { variant?: 'muted' }) => {
  const { setHasHeaderCloseButton, hideCloseButton } = React.useContext(DialogScrollContext);

  React.useLayoutEffect(() => {
    setHasHeaderCloseButton(true);
    return () => setHasHeaderCloseButton(false);
  }, [setHasHeaderCloseButton]);

  const isMuted = variant === 'muted';

  // When parent DialogContent has hideCloseButton, render children directly
  // without the flex wrapper or auto-injected close button (caller controls layout).
  if (hideCloseButton) {
    return (
      <div
        className={cn(
          'relative z-10 shrink-0',
          isMuted ? '-mx-6 -mt-6 px-6 pt-6 pb-5 mb-3 rounded-t-md bg-muted' : 'pb-2 bg-background',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative z-10 shrink-0',
        isMuted ? '-mx-6 -mt-6 px-6 pt-6 pb-5 mb-3 rounded-t-md bg-muted' : 'pb-2 bg-background',
        className,
      )}
      {...props}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col space-y-1.5 min-w-0">{children}</div>
        <DialogPrimitive.Close
          data-testid="dialog-x-close-btn"
          tabIndex={-1}
          className={closeButtonInline}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </div>
    </div>
  );
};
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, style, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const { canScrollDown } = React.useContext(DialogScrollContext);
  return (
    <div
      className={cn(
        'relative z-10 shrink-0 -mx-6 -mb-6 px-6 py-4 rounded-b-md transition-shadow duration-200 flex flex-col-reverse sm:flex-row sm:justify-end sm:items-center sm:gap-2',
        className,
      )}
      style={{
        boxShadow: canScrollDown
          ? '0 -2px 6px -1px color-mix(in srgb, var(--color-foreground) 8%, transparent)'
          : 'none',
        ...style,
      }}
      {...props}
    />
  );
};
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogScrollContext,
  DialogTitle,
  DialogTrigger,
};
